import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Edit2, Gift } from "lucide-react";
import { adminClient } from "./api";

const EMPTY = { title: "", product_ids: [], bundle_price: "", blurb: "", is_active: true, sort_order: 0 };

export default function BundlesAdmin({ products }) {
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminClient.get("/admin/merch/bundles").then(({ data }) => setBundles(data.items || [])).catch(() => toast.error("Could not load bundles"));
  useEffect(() => { load(); }, []);

  const start = (b) => {
    setEditing(b || null);
    setForm(b ? { title: b.title, product_ids: b.product_ids, bundle_price: b.bundle_price, blurb: b.blurb || "", is_active: b.is_active, sort_order: b.sort_order || 0 } : { ...EMPTY, sort_order: bundles.length + 1 });
    setOpen(true);
  };

  const toggle = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  const regular = form.product_ids.reduce((s, id) => { const p = products.find((x) => x.id === id); return s + Number(p?.effective_price ?? p?.price ?? 0); }, 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, bundle_price: parseFloat(form.bundle_price), sort_order: parseInt(form.sort_order, 10) || 0 };
      if (editing) await adminClient.put(`/admin/merch/bundles/${editing.id}`, payload);
      else await adminClient.post("/admin/merch/bundles", payload);
      toast.success(editing ? "Bundle updated" : "Bundle created");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bundle "${b.title}"?`)) return;
    await adminClient.delete(`/admin/merch/bundles/${b.id}`);
    toast.success("Bundle deleted");
    load();
  };

  return (
    <div className="space-y-4" data-testid="bundles-admin">
      <Card className="p-5 border-2 border-zinc-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-black text-lg text-[#0C2E2B] flex items-center gap-2"><Gift className="w-5 h-5" /> Bundle deals</h3>
            <p className="text-xs text-zinc-500">Pair two or more products at one price. Shoppers add the whole set in one tap; the discount is split across the items at checkout.</p>
          </div>
          <Button data-testid="bundle-new-btn" onClick={() => start(null)} className="rounded-md font-bold"><Plus className="w-4 h-4 mr-1" /> New bundle</Button>
        </div>
        {bundles.length === 0 ? <p className="text-sm text-zinc-500" data-testid="bundles-empty">No bundles yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bundles.map((b) => (
              <div key={b.id} data-testid={`bundle-row-${b.id}`} className={`rounded-md border-2 p-3 ${b.is_active ? "border-zinc-200" : "border-dashed border-zinc-300 opacity-60"}`}>
                <div className="flex gap-1 mb-2">{b.products.map((p) => <img key={p.id} src={p.image} alt="" className="w-14 h-14 rounded object-cover" />)}</div>
                <div className="font-bold text-[#0C2E2B]">{b.title}</div>
                <div className="text-xs text-zinc-600">{b.products.map((p) => p.title).join(" + ")}</div>
                <div className="text-sm mt-1"><span className="font-mono-data font-black text-red-600">${Number(b.bundle_price).toFixed(2)}</span> <s className="text-xs text-zinc-400">${Number(b.regular_total).toFixed(2)}</s> <span className="text-[10px] font-bold text-emerald-700">save ${Number(b.savings).toFixed(2)}</span>{!b.available && <span className="ml-2 text-[10px] font-bold text-red-600">(a product is hidden — not shown)</span>}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" data-testid={`bundle-edit-${b.id}`} onClick={() => start(b)} className="h-8 text-xs font-bold"><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" data-testid={`bundle-delete-${b.id}`} onClick={() => remove(b)} className="h-8 text-xs font-bold text-red-600"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="bundle-form">
          <DialogHeader><DialogTitle className="font-display font-black text-[#0C2E2B]">{editing ? "Edit bundle" : "New bundle"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Title</Label>
              <Input data-testid="bundle-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Tee + Cap Combo" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Products (pick 2+)</Label>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-auto">
                {products.map((p) => (
                  <label key={p.id} data-testid={`bundle-product-${p.id}`} className={`flex items-center gap-2 rounded-md border p-2 cursor-pointer ${form.product_ids.includes(p.id) ? "border-[#0C2E2B] bg-[#FFDF00]/20" : "border-zinc-200"}`}>
                    <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggle(p.id)} />
                    <img src={p.image} alt="" className="w-8 h-8 rounded object-cover" />
                    <span className="text-xs font-bold truncate flex-1">{p.title}</span>
                    <span className="text-[11px] text-zinc-500">${Number(p.effective_price ?? p.price).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Bundle price ($)</Label>
                <Input data-testid="bundle-price-input" type="number" step="0.01" min="0" required value={form.bundle_price} onChange={(e) => setForm({ ...form, bundle_price: e.target.value })} className="mt-1" />
                <p className="text-[10px] text-zinc-500 mt-1" data-testid="bundle-regular-total">Regular total: ${regular.toFixed(2)}{form.bundle_price ? ` · saves $${Math.max(0, regular - parseFloat(form.bundle_price || 0)).toFixed(2)}` : ""}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Blurb</Label>
                <Input data-testid="bundle-blurb-input" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} placeholder="Short pitch" className="mt-1" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="bundle-active-switch" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> {form.is_active ? "Live" : "Hidden"}</label>
              <Button type="submit" data-testid="bundle-save-btn" disabled={saving} className="rounded-md font-bold">{saving ? "Saving…" : editing ? "Save bundle" : "Create bundle"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
